import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, redDark, amber, amberLight, amberDark, useTheme } from '../tokens';
import { getDeadlineStatus, formatTimestamp } from '../helpers';
import BankRecPanel from './BankRecPanel';
import FXCalcPanel from './FXCalcPanel';

const teal = '#0D9488';
const tealLight = '#CCFBF1';
const purple = '#7C3AED';
const purpleLight = '#EDE9FE';
const purpleDark = '#5B21B6';

// Define the workflow steps for FX Calc (Bank Rec is now automated via BankRecPanel)
const FX_CALC_STEPS = [
  { taskIds: [14, 15], label: 'Gather FX Data', description: 'Obtain BoC closing exchange rate. Report all USD account balances.' },
  { taskIds: [16, 17], label: 'Calculate FX Adjustment', description: 'Calculate CAD equivalent of USD balances. Compute FX gain/loss.' },
  { taskIds: [18, 19], label: 'Post Journal Entry', description: 'Prepare JE using Sage import template. Import into Sage Intacct.' },
  { taskIds: [20], label: 'Document Source Data', description: 'Document BoC rates, USD balances, and GL extracts in Source Data tab.' },
];

const TABS = [
  { id: 'bank-rec', label: 'Bank Rec', sections: ['Cash - Bank Reconciliation', 'Cash - Float Reconciliation'] },
  { id: 'fx-calc', label: 'FX Calc', steps: FX_CALC_STEPS, sections: ['Cash - FX Calculation'] },
];

function StepCheckbox({ active, color, onClick, disabled, size = 18 }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        width: size, height: size, borderRadius: 4,
        border: `1.5px solid ${active ? color : '#CBD5E1'}`,
        background: active ? color : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: 'all 0.15s',
        flexShrink: 0,
      }}
    >
      {active && <span style={{ color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1 }}>\u2713</span>}
    </div>
  );
}

function WorkflowStep({ step, tasks, onToggleComplete, onToggleReview, onUpdateDeadline, mockToday, userInitials, t, stepIndex }) {
  const [expanded, setExpanded] = useState(false);

  // Aggregate status across all tasks in this step
  const allDone = tasks.every(tk => tk.done || tk.blocked);
  const allReviewed = tasks.every(tk => tk.reviewed);
  const anyBlocked = tasks.some(tk => tk.blocked);
  const anyOverdue = tasks.some(tk => !tk.blocked && !tk.done && getDeadlineStatus(tk.dueDate, tk.done, mockToday) === 'overdue');

  // For single-task steps, use that task directly
  const isSingle = tasks.length === 1;
  const singleTask = isSingle ? tasks[0] : null;

  // Can prep: not all done yet
  const canPrep = tasks.some(tk => !tk.done && !tk.blocked);
  // Can review: all done, not all reviewed, and reviewer is different from preparer
  const canReview = allDone && !allReviewed && tasks.some(tk => tk.done && !tk.reviewed && tk.completedBy !== userInitials);

  const handlePrepClick = (e) => {
    e.stopPropagation();
    if (isSingle && singleTask && !singleTask.done && !singleTask.blocked) {
      onToggleComplete(singleTask.id);
    }
  };

  const handleReviewClick = (e) => {
    e.stopPropagation();
    if (isSingle && singleTask && singleTask.done && !singleTask.reviewed && singleTask.completedBy !== userInitials) {
      onToggleReview(singleTask.id);
    }
  };

  // Earliest due date in this step
  const earliestDue = tasks
    .filter(tk => tk.dueDate)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0]?.dueDate;

  return (
    <div style={{
      border: `1px solid ${allReviewed ? green + '30' : anyBlocked ? purple + '20' : t.border}`,
      borderRadius: 8,
      marginBottom: 8,
      overflow: 'hidden',
      background: allReviewed ? green + '04' : t.cardBg,
    }}>
      {/* Step header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'grid',
          gridTemplateColumns: '28px 1fr auto 28px 28px 20px',
          alignItems: 'center',
          padding: '10px 12px',
          cursor: 'pointer',
          gap: 8,
        }}
      >
        {/* Step number */}
        <div style={{
          width: 22, height: 22, borderRadius: '50%',
          background: allReviewed ? green : allDone ? teal : anyBlocked ? purple + '20' : '#F1F5F9',
          color: allReviewed || allDone ? '#fff' : '#64748B',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700, fontFamily: mono,
        }}>
          {allReviewed ? '\u2713' : stepIndex + 1}
        </div>

        {/* Step label + description */}
        <div>
          <div style={{
            fontSize: 12, fontWeight: 600, color: allReviewed ? t.textMuted : t.textPrimary,
            fontFamily: sans, textDecoration: allReviewed ? 'line-through' : 'none',
          }}>
            {step.label}
          </div>
          <div style={{ fontSize: 10, color: t.textFaint, marginTop: 1 }}>
            {tasks.length} task{tasks.length > 1 ? 's' : ''}
            {earliestDue && <> &middot; Due {new Date(earliestDue + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>}
            {anyOverdue && <span style={{ color: red, fontWeight: 600 }}> &middot; Overdue</span>}
            {anyBlocked && <span style={{ color: purple, fontWeight: 600 }}> &middot; Blocked</span>}
          </div>
        </div>

        {/* Initials */}
        <div style={{ display: 'flex', gap: 3 }}>
          {tasks.filter(tk => tk.completedBy).map(tk => (
            <span key={tk.id + '-p'} style={{
              fontSize: 8, fontFamily: mono, fontWeight: 700, color: '#fff',
              background: teal, borderRadius: 3, padding: '1px 4px',
            }}>{tk.completedBy}</span>
          )).filter((_, i) => i === 0)}
          {tasks.filter(tk => tk.reviewedBy).map(tk => (
            <span key={tk.id + '-r'} style={{
              fontSize: 8, fontFamily: mono, fontWeight: 700, color: '#fff',
              background: green, borderRadius: 3, padding: '1px 4px',
            }}>{tk.reviewedBy}</span>
          )).filter((_, i) => i === 0)}
        </div>

        {/* Prep checkbox */}
        <StepCheckbox
          active={allDone}
          color={teal}
          onClick={handlePrepClick}
          disabled={!isSingle || !canPrep}
        />

        {/* Review checkbox */}
        <StepCheckbox
          active={allReviewed}
          color={green}
          onClick={handleReviewClick}
          disabled={!isSingle || !canReview}
        />

        {/* Expand chevron */}
        <span style={{
          fontSize: 12, color: t.textFaint, textAlign: 'center',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s', display: 'inline-block',
        }}>\u25BE</span>
      </div>

      {/* Expanded: show individual tasks + details */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${t.border}`, padding: '10px 12px' }}>
          {/* Description */}
          <div style={{
            fontSize: 11, lineHeight: 1.5, color: t.textSecondary,
            padding: '8px 10px', background: t.bgSurface || '#F8FAFC', borderRadius: 6,
            marginBottom: 10, border: `1px solid ${t.border}`,
          }}>
            {step.description}
          </div>

          {/* Individual tasks (for multi-task steps) */}
          {tasks.length > 1 && tasks.map(task => {
            const canCompleteSingle = !task.done && !task.blocked;
            const canReviewSingle = task.done && !task.reviewed && task.completedBy !== userInitials;
            const dlStatus = task.blocked ? 'blocked' : getDeadlineStatus(task.dueDate, task.done, mockToday);

            return (
              <div key={task.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 60px 24px 24px',
                alignItems: 'center',
                padding: '6px 8px',
                borderBottom: `1px solid ${t.border}`,
                gap: 6,
              }}>
                <div>
                  <div style={{
                    fontSize: 11, fontFamily: sans, fontWeight: 500,
                    color: task.reviewed ? t.textMuted : t.textPrimary,
                    textDecoration: task.reviewed ? 'line-through' : 'none',
                  }}>
                    {task.name}
                  </div>
                  {task.accounts && (
                    <div style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>{task.accounts}</div>
                  )}
                </div>
                <span style={{
                  fontSize: 9, fontFamily: mono, textAlign: 'right',
                  color: dlStatus === 'overdue' ? red : dlStatus === 'due-soon' ? amber : t.textFaint,
                }}>
                  {task.dueDate ? new Date(task.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '\u2014'}
                </span>
                <StepCheckbox
                  active={task.done}
                  color={teal}
                  onClick={() => canCompleteSingle && onToggleComplete(task.id)}
                  disabled={!canCompleteSingle}
                  size={16}
                />
                <StepCheckbox
                  active={task.reviewed}
                  color={green}
                  onClick={() => canReviewSingle && onToggleReview(task.id)}
                  disabled={!canReviewSingle}
                  size={16}
                />
              </div>
            );
          })}

          {/* Documents */}
          {tasks.some(tk => tk.documents && tk.documents.length > 0) && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Documents</div>
              {tasks.flatMap(tk => (tk.documents || []).map((doc, i) => (
                <div key={`${tk.id}-${i}`} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                  border: `1px solid ${t.border}`, borderRadius: 4, marginBottom: 3, fontSize: 10,
                }}>
                  <span>📄</span>
                  <span style={{ flex: 1, color: t.textPrimary }}>{doc}</span>
                </div>
              )))}
            </div>
          )}

          {/* Workbook */}
          {tasks.some(tk => tk.workbook) && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Workbook</div>
              {[...new Set(tasks.filter(tk => tk.workbook).map(tk => tk.workbook))].map(wb => (
                <div key={wb} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  border: `1px solid ${greenLight}`, background: greenLight + '20', borderRadius: 6,
                }}>
                  <span>📊</span>
                  <span style={{ fontSize: 11, color: greenDark, fontWeight: 500 }}>{wb}</span>
                </div>
              ))}
            </div>
          )}

          {/* Deadline editor (for single-task steps) */}
          {isSingle && singleTask && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Deadline</div>
              <input
                type="date"
                value={singleTask.dueDate || ''}
                onChange={e => onUpdateDeadline(singleTask.id, e.target.value)}
                style={{
                  fontFamily: mono, fontSize: 11, color: t.textPrimary,
                  background: 'transparent', border: `1px solid ${t.border}`,
                  borderRadius: 4, padding: '3px 6px',
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CashWorkspace({ tasks, onClose, onToggleComplete, onToggleReview, onToggleBlocked, onUpdateDeadline, mockToday, userInitials, companyId, selectedMonth }) {
  const t = useTheme();
  const [activeTab, setActiveTab] = useState('bank-rec');
  const month = selectedMonth || '2026-02';

  const currentTab = TABS.find(tab => tab.id === activeTab);

  // Overall Cash stats
  const done = tasks.filter(tk => tk.done).length;
  const reviewed = tasks.filter(tk => tk.reviewed).length;
  const blocked = tasks.filter(tk => tk.blocked).length;
  const total = tasks.length;

  // Tab-specific stats
  const tabStats = useMemo(() => {
    const stats = {};
    TABS.forEach(tab => {
      const tabTasks = tasks.filter(tk => tab.sections.includes(tk.section));
      const tabDone = tabTasks.filter(tk => tk.done).length;
      const tabReviewed = tabTasks.filter(tk => tk.reviewed).length;
      const tabTotal = tabTasks.length;
      stats[tab.id] = { done: tabDone, reviewed: tabReviewed, total: tabTotal, allDone: tabDone === tabTotal && tabTotal > 0, allReviewed: tabReviewed === tabTotal && tabTotal > 0 };
    });
    return stats;
  }, [tasks]);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: activeTab === 'bank-rec' ? '85vw' : 520,
      maxWidth: activeTab === 'bank-rec' ? 1200 : 520,
      background: t.bgCard || '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      zIndex: 100, display: 'flex', flexDirection: 'column', fontFamily: sans,
      transition: 'width 0.3s ease',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary }}>Cash & Cash Equivalents</div>
            <div style={{ fontSize: 10, fontFamily: mono, color: t.textFaint, marginTop: 2 }}>GL 11010\u201311110</div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: t.inputBg || '#F1F5F9', borderRadius: 6, width: 28, height: 28,
            cursor: 'pointer', fontSize: 14, color: t.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>&times;</button>
        </div>

        {/* Overall progress */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: green }}>{reviewed}</div>
            <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Reviewed</div>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: teal }}>{done}</div>
            <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Prepared</div>
          </div>
          {blocked > 0 && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: purple }}>{blocked}</div>
              <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Blocked</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: t.textSecondary }}>{total}</div>
            <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Total</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: '#E2E8F0', borderRadius: 2, overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${total > 0 ? (reviewed / total) * 100 : 0}%`, background: green, transition: 'width 0.3s' }} />
          <div style={{ width: `${total > 0 ? ((done - reviewed) / total) * 100 : 0}%`, background: teal, transition: 'width 0.3s' }} />
          <div style={{ width: `${total > 0 ? (blocked / total) * 100 : 0}%`, background: purple, transition: 'width 0.3s' }} />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginTop: 14 }}>
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            const stats = tabStats[tab.id];
            const tabAccent = stats?.allReviewed ? green : stats?.allDone ? teal : active ? '#333' : t.textMuted;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding: '6px 14px', borderRadius: 6,
                border: `1px solid ${active ? tabAccent : t.border}`,
                background: active ? (stats?.allReviewed ? greenLight : stats?.allDone ? tealLight : '#F8FAFC') : 'transparent',
                color: tabAccent,
                fontSize: 11, fontWeight: active ? 700 : 500, cursor: 'pointer', fontFamily: sans,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {tab.label}
                <span style={{ fontSize: 9, fontFamily: mono, opacity: 0.7 }}>
                  {stats?.reviewed}/{stats?.total}
                </span>
                {stats?.allReviewed && <span style={{ fontSize: 10 }}>{'\u2713'}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
        {activeTab === 'bank-rec' && (
          <BankRecPanel companyId={companyId} month={month} />
        )}
        {activeTab === 'fx-calc' && (
          <FXCalcPanel month={month} />
        )}
      </div>
    </div>
  );
}
